import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  BulletList,
  ClaimSummaryPageIcon,
  Image,
  OtherChangesIcon,
  SmartClaimPathsIcon,
  Text,
  useResponsive
} from '@common-fe/common-fe';

import DarkLogo from '@/assets/dark-logo.svg';
import { DismissedBannerKey, LARGE_SIZE_LOGO } from '@/common/constants';
import { Modal } from '@/components';
import theme from '@/styles/theme';
import { whiteLabelingConfig } from '@/utils/whiteLabeling';

import SeeWhatsNewPopupWrapper from './SeeWhatsNewPopupWrapper';

interface Props {
  onSetVisible: (value: boolean) => void;
  dismissBanner?: (bannerKey: DismissedBannerKey) => void;
}

const SeeWhatsNewPopup: React.FC<Props> = ({ onSetVisible, dismissBanner }) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [isCloseForever, setIsCloseForever] = useState(false);

  const handleClose = useCallback(() => {
    onSetVisible(false);
    if (isCloseForever) {
      dismissBanner?.(DismissedBannerKey.USE_MY_MONEY_SEE_WHATS_NEW);
    }
  }, [onSetVisible, dismissBanner, isCloseForever]);

  const handleCloseForever = useCallback(() => {
    onSetVisible(false);
    dismissBanner?.(DismissedBannerKey.USE_MY_MONEY_SEE_WHATS_NEW);
  }, [onSetVisible, dismissBanner]);

  return (
    <Modal
      visible
      onSetVisible={handleClose}
      testId="see-whats-new"
      contentStyle={{
        padding: 0,
        marginLeft: isMobile ? theme.spacings.spacing20 : undefined,
        marginRight: isMobile ? theme.spacings.spacing20 : undefined,
      }}
    >
      <SeeWhatsNewPopupWrapper
        onClose={handleClose}
        isCloseForever={isCloseForever}
        setIsCloseForever={setIsCloseForever}
      >
        <Box margin={{ top: isMobile ? 'spacing8' : 'spacing32' }} align="center">
          <Image
            src={whiteLabelingConfig?.logo || DarkLogo}
            width={LARGE_SIZE_LOGO.width}
            height={LARGE_SIZE_LOGO.height}
            alt="logo"
          />
        </Box>
        <Text
          margin={{ top: isMobile ? 'spacing16' : 'spacing48' }}
          size={isMobile ? '3xl' : '64px'}
          weight={700}
          textAlign="center"
          style={{ lineHeight: '110%' }}
        >
          {t('It’s now easier')}
          <br />
          {t('To use your funds')}
        </Text>

        <Box
          margin={{ top: isMobile ? 'spacing24' : 'spacing48' }}
          direction={isMobile ? 'column' : 'row'}
          style={{ gap: theme.spacings.spacing12 }}
        >
          <Box
            width={isMobile ? undefined : "50%"}
            background="whatIsNew3"
            pad={isMobile ? 'spacing24' : { horizontal: 'spacing32', top: 'spacing32', bottom: 'spacing48' }} 
            round="container2Round"
          >
            <Box direction={isMobile ? 'row' : 'column'} align={isMobile ? 'center' : 'start'}>
              <SmartClaimPathsIcon color="iconOnColor" size={isMobile ? '40px' : '48px'} />
              <Text
                margin={isMobile ? { left: 'spacing12' } : { top: 'spacing16' }}
                color="textOnColor"
                size={isMobile ? '2xl' : '3xl'}
                weight={700}
              >
                {t('Smarter Claims')}
              </Text>
            </Box>
            <Text color="textOnColor" margin={{ top: 'spacing12' }}>
              {t('Claims aren’t one-size-fits-all. You’ll be guided through the right steps as you submit your claim.')}
            </Text>
          </Box>

          <Box
            width={isMobile ? undefined : "50%"}
            background="whatIsNew2"
            pad={isMobile ? 'spacing24' : { horizontal: 'spacing32', top: 'spacing32', bottom: 'spacing48' }} 
            round="container2Round"
          >
            <Box direction={isMobile ? 'row' : 'column'} align={isMobile ? 'center' : 'start'}>
              <ClaimSummaryPageIcon color="iconOnColor" size={isMobile ? '40px' : '48px'} />
              <Text
                margin={isMobile ? { left: 'spacing12' } : { top: 'spacing16' }}
                color="textOnColor"
                size={isMobile ? '2xl' : '3xl'}
                weight={700}
              >
                {t('Claim Summary')}
              </Text>
            </Box>
            <Text color="textOnColor" margin={{ top: 'spacing12' }}>
              {t('You’ll see a new page that summarizes your claim details as you move through the process.')}
            </Text>
          </Box>
        </Box>

        <Box
          margin={{ top: 'spacing12' }}
          pad={isMobile ? 'spacing24' : { vertical: 'spacing48', horizontal: 'spacing32' }}
          background="background1"
          round="container2Round"
          style={{ gap: theme.spacings.spacing24 }}
          direction={isMobile ? 'column' : 'row'}
        >
          <Box width={isMobile ? undefined : "45%"}>
            <Text
              color="textOnColor"
              size={isMobile ? '2xl' : '3xl'}
              weight={700}
            >
              {t('Other Changes')}
            </Text>
            <BulletList margin={{ top: 'spacing12' }}>
              <BulletList.Item
                textProps={{ color: 'textOnColor' }}
                bulletProps={{ background: 'iconOnColor', style: { opacity: 0.1 } }}
              >
                {t('Account balances now show on claim screens, so you know how much you have')}
              </BulletList.Item>
              <BulletList.Item
                textProps={{ color: 'textOnColor' }}
                bulletProps={{ background: 'iconOnColor', style: { opacity: 0.1 } }}
              >
                {t('Account balances show, so you can see how much you have')}
              </BulletList.Item>
              <BulletList.Item
                textProps={{ color: 'textOnColor' }}
                bulletProps={{ background: 'iconOnColor', style: { opacity: 0.1 } }}
              >
                {t('Estimated payment times are posted, so you know when to expect your money')}
              </BulletList.Item>
            </BulletList>
          </Box>
          <Box width={isMobile ? undefined : "55%"}>
            <OtherChangesIcon style={{ width: '100%' }} />
          </Box>
        </Box>

        <Box margin={{ top: 'spacing24' }}>
          <AppButton
            testId="close-and-dont-show-again"
            onClick={handleCloseForever}
            size="L"
            width="100%"
          >
            {t('Close and Don’t Show Again')}
          </AppButton>
        </Box>
      </SeeWhatsNewPopupWrapper>
    </Modal>
  );
};

export default SeeWhatsNewPopup;
